// VoIP History API Service
import axios from 'axios';
import { APP_BASE_URL, GET_TELEMETRY_HISTORY } from '../../../Common/EndPoints';

export interface VoIPHistoryParams {
  deviceId: string;
  startTs?: number;      // Start timestamp (ms)
  endTs?: number;        // End timestamp (ms)
  page?: number;
  pageSize?: number;
}

export interface VoIPHistoryRecord {
  id: string;
  timestamp: string;
  status: 'online' | 'offline';
  latency: number;
  health: string;
  message: string;
  rawData: Record<string, any>;
}

export interface VoIPHistoryResponse {
  content: any[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
}

/**
 * Fetch VoIP device history from telemetry API
 */
export const fetchVoIPHistory = async (params: VoIPHistoryParams): Promise<VoIPHistoryResponse> => {
  try {
    const response = await axios.get(`${APP_BASE_URL}${GET_TELEMETRY_HISTORY}`, {
      params: {
        deviceId: params.deviceId,
        startTs: params.startTs,
        endTs: params.endTs,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        isModelView: true,
      },
    });

    const data = response.data?.data;
    return {
      content: data?.content || [],
      totalElements: data?.totalElements || 0,
      totalPages: data?.totalPages || 0,
      currentPage: data?.number || 0,
    };
  } catch (error) {
    console.error('Error fetching VoIP history:', error);
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: 0,
    };
  }
};

/**
 * Transform raw history data to VoIPHistoryRecord format
 */
export const transformHistoryData = (rawContent: any[]): VoIPHistoryRecord[] => {
  return rawContent.map((item, index) => {
    const dataItem = item?.data?.[0] || {};
    const values = dataItem?.values || {};
    const dateTime = dataItem?.dateTime || item?.ts || Date.now();

    // Determine status from values
    const health = values['asset.health'] || '';
    const isOnline = health.toUpperCase() === 'HEALTHY';

    return {
      id: `history-${index}-${dateTime}`,
      timestamp: new Date(dateTime).toISOString(),
      status: isOnline ? 'online' : 'offline',
      latency: parseFloat(values['LATENCY'] || values['RTT'] || '0') || 0,
      health: health || 'Unknown',
      message: values['responseMeta']?.message || '-',
      rawData: values,
    };
  });
};

/**
 * Get quick date range presets
 */
export const getDateRangePresets = () => [
  { label: 'Last 24 Hours', days: 1 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 90 Days', days: 90 },
];

/**
 * Calculate date range from preset
 */
export const calculateDateRange = (days: number): { startTs: number; endTs: number } => {
  const endTs = Date.now();
  const startTs = endTs - (days * 24 * 60 * 60 * 1000);
  return { startTs, endTs };
};
